import {GeoSearchControl, OpenStreetMapProvider} from "leaflet-geosearch";
import {mapTiles} from "@/composables/mapConfig";

// Unit and suite parts OpenStreetMap does not index: "Suite 100", "Ste #2B",
// "Apt 4", "Unit B", "#300". With them in the query the whole address comes
// back empty, so a submitted search that finds nothing tries once more
// without them. Only a keyword followed by a unit number or a single letter
// counts, so "Unit Rd" or a house number after it is left alone. Floor and
// building are not stripped ("2nd Floor 9 Elm St", and FL is Florida).
const UNIT_PART = /[,\s]*(?:\b(?:suite|ste|unit|apt|apartment|rm|room)\b\.?\s*#?|#)\s*(?:[\w-]*\d[\w-]*|[a-z])\b/gi;

export const stripUnit = (query) => query.replace(UNIT_PART, '').replace(/\s{2,}/g, ' ').replace(/\s+,/g, ',').trim();

// Nominatim allows one request a second; a search that has to fall back
// waits this long between tries.
const RETRY_GAP_MS = 1100;
const pause = () => new Promise(resolve => setTimeout(resolve, RETRY_GAP_MS));

// How far around the map centre a pressed-Enter search looks first, in
// degrees (about 150 km). "North Point Mall" should be the one down the road,
// not the one in Bogota that OpenStreetMap ranks first.
const NEARBY_DEGREES = 1.5;

/**
 * OpenStreetMap search tied to the map. Suggestions while typing prefer what
 * the map shows (a viewbox is a bias, not a filter); searchNearby() only
 * returns results around the map centre.
 */
class NearbyAddressProvider extends OpenStreetMapProvider {
    constructor(map) {
        // Endpoints come from the server (API .env) via mapConfig, not the
        // library's hardcoded nominatim.openstreetmap.org defaults.
        super({searchUrl: mapTiles.searchUrl, reverseUrl: mapTiles.reverseUrl});
        this.map = map;
    }

    viewbox(around = 0) {
        const b = this.map?.getBounds?.();
        if (!b) return null;
        const c = b.getCenter();
        const west = Math.min(b.getWest(), c.lng - around), east = Math.max(b.getEast(), c.lng + around);
        const south = Math.max(Math.min(b.getSouth(), c.lat - around), -90), north = Math.min(Math.max(b.getNorth(), c.lat + around), 90);
        return [west, north, east, south].join(',');
    }

    search({query}) {
        const viewbox = this.viewbox();
        return super.search({query: viewbox ? {q: query, viewbox} : query});
    }

    searchNearby(query) {
        const viewbox = this.viewbox(NEARBY_DEGREES);
        return viewbox ? super.search({query: {q: query, viewbox, bounded: 1}}) : Promise.resolve([]);
    }
}

/**
 * The address bar on a Leaflet map. A submitted search that finds nothing
 * calls onNotFound instead of silently doing nothing.
 */
export function addressSearchControl(map, {searchLabel = 'Search address...', onNotFound} = {}) {
    const provider = new NearbyAddressProvider(map);
    const control = new GeoSearchControl({
        style: 'bar',
        autoClose: true,
        keepResult: true,
        showMarker: false,
        searchLabel,
        provider,
    });

    // The stock submit searches again and ignores an empty answer. A picked
    // suggestion already carries its result, so use it as is.
    let latest = 0;
    control.onSubmit = async function (query) {
        this.resultList.clear();
        const run = ++latest;
        if (query?.data) {
            this.showResult(query.data, query);
            return;
        }
        const text = (query?.query ?? '').trim();
        if (!text) return;
        // Near the map first, then anywhere, then the same without a suite or
        // unit. Stops at the first answer, so a normal search is one request.
        const attempts = [() => provider.searchNearby(text), () => provider.search({query: text})];
        const shorter = stripUnit(text);
        if (shorter && shorter !== text) {
            attempts.push(() => provider.searchNearby(shorter), () => provider.search({query: shorter}));
        }
        try {
            let results = [];
            for (const [i, attempt] of attempts.entries()) {
                if (i) await pause();
                // A newer search was submitted meanwhile; let that one decide.
                if (run !== latest) return;
                results = await attempt();
                if (results?.length) break;
            }
            if (run !== latest) return;
            if (results?.length) this.showResult(results[0], query);
            else onNotFound?.(text);
        } catch {
            if (run === latest) onNotFound?.(text);
        }
    };

    return control;
}
